import { fmt } from "../../format";
import { type Theme, useTheme } from "../../theme";
import { DropdownMenu, type MenuEntry } from "../DropdownMenu/DropdownMenu";
import type { TopbarUser } from "./topbarTypes";

const USER_MENU_MIN_WIDTH = 248;

interface TopbarUserMenuProps {
  user: TopbarUser;
  onProfile?: () => void;
  onLogout?: () => void;
}

/** The person's name and avatar, opening a menu with the account, the theme and sign out. */
export function TopbarUserMenu({ user, onProfile, onLogout }: TopbarUserMenuProps): React.JSX.Element {
  const { theme, toggleTheme } = useTheme();
  const items: MenuEntry[] = [
    { node: <UserMenuHead user={user} /> },
    "separator",
    { label: "My account", icon: "user", onSelect: () => onProfile?.() },
    themeEntry(theme, toggleTheme),
    "separator",
    { label: "Sign out", icon: "logout", onSelect: () => onLogout?.() },
  ];
  return (
    <DropdownMenu
      ariaLabel="User menu"
      minWidth={USER_MENU_MIN_WIDTH}
      items={items}
      trigger={(props) => (
        <button type="button" className="nb-user" aria-label={`Menu for ${user.name}`} {...props}>
          <span className="nb-user-meta">
            <strong>{user.name}</strong>
            {user.role ? <span>{user.role}</span> : null}
          </span>
          <UserAvatar name={user.name} />
        </button>
      )}
    />
  );
}

function themeEntry(theme: Theme, toggleTheme: () => void): MenuEntry {
  const dark = theme === "dark";
  return { label: dark ? "Light theme" : "Dark theme", icon: dark ? "sun" : "moon", onSelect: () => toggleTheme() };
}

function UserMenuHead({ user }: { user: TopbarUser }): React.JSX.Element {
  return (
    <div className="nb-menu-head">
      <UserAvatar name={user.name} />
      <div className="nb-truncate">
        <strong className="nb-truncate">{user.name}</strong>
        {user.email ? <span className="nb-menu-head-email nb-truncate">{user.email}</span> : null}
      </div>
    </div>
  );
}

function UserAvatar({ name }: { name: string }): React.JSX.Element {
  return <span className="nb-avatar nb-avatar-navy">{fmt.initials(name)}</span>;
}
